import { type Opportunity, type PartnerOrganization } from "@entities";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import { TuneRounded } from "@mui/icons-material";
import { Button, Stack } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import dayjs from "dayjs";
import { type FC, useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { PartnerOrganizationDetailsDialog } from "../tables/partner-organizations-table/PartnerOrganizationDetailsDialog";
import { loadPartnerOrganizationForDetails } from "../tables/partner-organizations-table/partner-organization-details.helper";
import { CommunitySelect } from "../../components/add-request/CommunitySelect";
import { DateAndHoursRange } from "../../components/add-request/date-and-hours/DateAndHoursRange";
import EventImageSection from "../../components/event-image-section/EventImageSection";
import { InfoForUser } from "../../components/info-for-user/InfoForUser";
import {
  ADMIN_GROUP_DETAILS_SECTION_HINT,
  ADMIN_GROUP_DETAILS_SECTION_TITLE,
} from "@shared/helpers/opportunity-group-admin-copy";
import { useRequest } from "../../contexts/RequestProvider";
import OrganizationRequestButtons from "./OrganizationRequestButtons";
import { GroupOpportunitySlots } from "../../components/group-opportunity-slots/GroupOpportunitySlots";
import { OpportunityFormSection } from "./OpportunityFormSection";
import { OpportunityRequesterAssociationSection } from "./OpportunityRequesterAssociationSection";
import type { OrganizationRequestSaveResult } from "./OrganizationRequest";

export const DetailsSection: FC<{
  showExtendedLayout?: boolean;
  isStandalonePage?: boolean;
  isEdit?: boolean;
  eventDetails?: Opportunity;
  requestDetails?: Partial<Opportunity>;
  setIsAddEvent?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAddAidRelated?: Dispatch<SetStateAction<boolean>>;
  onSaveSuccess?: (result?: OrganizationRequestSaveResult) => void;
}> = ({
  isEdit,
  eventDetails,
  showExtendedLayout = false,
  isStandalonePage = false,
  requestDetails,
  setIsAddEvent,
  setIsAddAidRelated = () => {},
  onSaveSuccess,
}) => {
    const { setCurrentEvent: setRequestDetails } = useRequest();
    const navigate = useNavigate();
    const [partnerOrgDialogOpen, setPartnerOrgDialogOpen] = useState(false);
    const [partnerOrgForDialog, setPartnerOrgForDialog] =
      useState<PartnerOrganization | null>(null);

    const partnerOrgId = requestDetails?.partnerOrganizationId;
    const partnerOrgName =
      requestDetails?.partnerOrganization?.name ?? "ארגון שותף";

    const openPartnerOrganizationDetails = useCallback(async () => {
      if (!partnerOrgId) return;
      const org = await loadPartnerOrganizationForDetails(
        partnerOrgId,
        requestDetails?.partnerOrganization,
      );
      setPartnerOrgForDialog(org);
      setPartnerOrgDialogOpen(true);
    }, [partnerOrgId, requestDetails?.partnerOrganization]);

    useEffect(() => {
      if (isEdit && requestDetails?.id) return;
      const start = requestDetails?.startDate;
      const end = requestDetails?.endDate;
      const last = requestDetails?.lastCallToVolunteers;
      let next: Date;
      if (start) {
        next = dayjs(start).subtract(3, "hour").toDate();
      } else if (end) {
        next = dayjs(end).endOf("day").toDate();
      } else if (last) {
        next = dayjs(last).endOf("day").toDate();
      } else {
        next = dayjs().add(1, "month").toDate();
      }
      setRequestDetails((prev) => {
        const prevLast = prev.lastCallToVolunteers;
        if (prevLast && Math.abs(prevLast.getTime() - next.getTime()) < 60_000) return prev;
        return { ...prev, lastCallToVolunteers: next };
      });
    }, [isEdit, requestDetails?.id, requestDetails?.startDate, requestDetails?.endDate]);

    const showRequesterInMainColumn =
      !showExtendedLayout &&
      (requestDetails?.status === "AID_REQUEST" ||
        !!requestDetails?.associationRequestedId ||
        !!requestDetails?.associationRequested);

    return (
      <Stack
        className="organization-request-details-section"
        width="100%"
        height="100%"
        sx={{ minHeight: 0, overflow: "hidden" }}
      >
        <PartnerOrganizationDetailsDialog
          open={partnerOrgDialogOpen}
          organization={partnerOrgForDialog}
          onClose={() => {
            setPartnerOrgDialogOpen(false);
            setPartnerOrgForDialog(null);
          }}
        />
        <Stack
          className="organization-request-details-scroll"
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: showExtendedLayout ? 2 : "10%",
            py: 1,
            gap: 2,
          }}
        >
          <OpportunityFormSection
            title="פרטי המשימה"
            description="מידע שיוצג למתנדבים בכרטיסיית ההתנדבות"
          >
            <Stack className="organization-request-image-wrap" sx={{ width: "100%" }}>
              <EventImageSection />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="flex-end" flexWrap="wrap" gap={1}>
              {partnerOrgId && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CorporateFareIcon />}
                  onClick={() => void openPartnerOrganizationDetails()}
                  sx={{ borderRadius: "1rem", whiteSpace: "nowrap" }}
                >
                  {partnerOrgName}
                </Button>
              )}
              {!showExtendedLayout && (
                <Button
                  onClick={() =>
                    navigate("/events/new", {
                      state: requestDetails,
                    })
                  }
                  color="info"
                  variant="contained"
                  sx={{
                    borderRadius: "1rem",
                    alignItems: "center",
                    gap: "0.2rem",
                    color: "white",
                  }}
                >
                  <TuneRounded fontSize="small" />
                  הגדרות נוספות
                </Button>
              )}
            </Stack>
            <InfoForUser />
          </OpportunityFormSection>

          {showRequesterInMainColumn && (
            <OpportunityRequesterAssociationSection
              setIsAddAidRelated={setIsAddAidRelated}
            />
          )}

          {requestDetails?.isOpportunityGroupParent ? (
            <OpportunityFormSection
              title={ADMIN_GROUP_DETAILS_SECTION_TITLE}
              description={
                requestDetails.id
                  ? "אלה המופעים של הבקשה. לכל מופע יש מועד ומיקום משלו."
                  : `תאריך, מיקום ומועד אחרון להרשמה מוגדרים לכל מופע בנפרד. ${ADMIN_GROUP_DETAILS_SECTION_HINT}`
              }
            >
              {requestDetails.id ? (
                <GroupOpportunitySlots
                  parentId={requestDetails.id}
                  parentTitle={requestDetails.title}
                />
              ) : null}
            </OpportunityFormSection>
          ) : (
            <OpportunityFormSection title="מועד">
              <DateAndHoursRange
                isRepeated={
                  requestDetails?.isRepeated && !!eventDetails?.repeatedOpportunity
                }
                isEdit={isEdit}
                past={isEdit || eventDetails?.status === "AID_REQUEST"}
                names={["startDate", "endDate"]}
              />
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                localeText={heIL.components.MuiLocalizationProvider.defaultProps.localeText}
                adapterLocale="he"
              >
                <DateTimePicker
                  label="מועד אחרון להרשמת מתנדבים"
                  format="DD/MM/YYYY HH:mm"
                  value={requestDetails?.lastCallToVolunteers ? dayjs(requestDetails.lastCallToVolunteers) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      setRequestDetails((prev) => ({
                        ...prev,
                        lastCallToVolunteers: newValue.toDate(),
                      }));
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: { maxWidth: 320 },
                    },
                  }}
                />
              </LocalizationProvider>
            </OpportunityFormSection>
          )}

          <OpportunityFormSection title="התאמה">
            <CommunitySelect />
          </OpportunityFormSection>
        </Stack>

        {!showExtendedLayout && (
          <Stack
            className="organization-request-actions-bar"
            width="100%"
            direction="row"
            alignItems="center"
            justifyContent="end"
            gap={2}
            sx={{ flexShrink: 0, px: 2.5, py: 1.5 }}
          >
            <OrganizationRequestButtons
              isEdit={isEdit}
              isStandalonePage={isStandalonePage}
              setIsAddEvent={setIsAddEvent}
              onSaveSuccess={onSaveSuccess}
            />
          </Stack>
        )}
      </Stack>
    );
  };
